import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { TemplateSearch } from '@/components/Modals/TemplateGalleryModal/TemplateGalleryList/TemplateSearch';
import Ghost from '@/images/Ghost.svg';
import { PolotnoStoreKeys, usePolotnoState } from '@/pages/AdvancedEditor/hooks/polotno-store';
import theme from '@/theme';
import { IProjectTemplate } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTemplateFilters, useTemplates } from '../hooks';
import { useTemplatesTagList } from '../hooks/useTemplatesTagList';
import { TemplateTile } from './TemplateTile';
import { TemplatesTabs } from './TemplatesTabs';

const TemplatesListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
`;

const TemplatesGridContainer = styled.div`
    height: calc(100% - 36px - 37px - 32px);
    overflow: auto;
    scrollbar-gutter: stable;
`;

const TemplatesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const NoTemplatesContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: ${theme.colors.black[700]};
    font-size: 16px;
    margin-top: 100px;

    svg {
        margin-bottom: 16px;
    }
`;

interface Props {
    setSelectedTemplate: (template: IProjectTemplate, index: number) => void;
    search: string;
    setSearch: (search: string) => void;
    filter: string;
    setFilter: (filter: string) => void;
}

export const TemplatesList = observer(({ setSelectedTemplate, search, setSearch, filter, setFilter }: Props) => {
    const { data: templates = [], isLoading } = useTemplates();
    const [cropSettings] = usePolotnoState(PolotnoStoreKeys.cropSettings);
    const { filteredTemplates, filteredByAspectRatioTemplates } = useTemplateFilters({
        templates,
        search,
        tagFilter: filter,
        aspectRatio: cropSettings,
    });

    const tagList = useTemplatesTagList(filteredByAspectRatioTemplates);

    return (
        <TemplatesListContainer>
            <TemplateSearch search={search} setSearch={setSearch} location="side-panel" />
            <TemplatesTabs filter={filter} setFilter={setFilter} tagList={tagList} />

            {isLoading && (
                <LoaderWrapper>
                    <CircularProgress />
                </LoaderWrapper>
            )}

            {!!search && !isLoading && !filteredTemplates.length && (
                <NoTemplatesContainer>
                    <MaskedIcon src={Ghost} size="32px" color={theme.colors.black[700]} />
                    <p>We couldn’t find templates for</p>
                    <p>
                        <strong>“{search}”</strong>
                    </p>
                </NoTemplatesContainer>
            )}

            {!isLoading && (
                <TemplatesGridContainer>
                    <TemplatesGrid id="templates-grid">
                        {filteredTemplates.map((template, index) => (
                            <TemplateTile key={template.id} onClick={() => setSelectedTemplate(template, index)} template={template} />
                        ))}
                    </TemplatesGrid>
                </TemplatesGridContainer>
            )}
        </TemplatesListContainer>
    );
});
